import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { useGetBranches } from "src/api/generic";
import {
  useGetProfitAndLossReportData,
  useGetProfitAndLossReportDownload,
} from "src/api/reports/profit-and-loss";
import { downloadAsXLS } from "src/utils";
import DateRangeInput from "src/shared/components/date-range";
import {
  ReportControlBar,
  ReportPageHeading,
  ReportSectionCard,
} from "src/shared/components/report-layout";
import { authState } from "src/store/auth";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import ProfitAndLossTable from "./table";

type ProfitAndLossFilters = {
  startDate: string;
  endDate: string;
  branchId: string;
};

type QueryParams = {
  start_date: string;
  end_date: string;
  branch_id?: number;
};

const buildQueryParams = (
  startDate: string,
  endDate: string,
  branchIdParam: string,
): QueryParams => {
  const branchIdNum = branchIdParam ? Number(branchIdParam) : undefined;
  return {
    start_date: startDate,
    end_date: endDate,
    branch_id: Number.isFinite(branchIdNum) ? branchIdNum : undefined,
  };
};

const ProfitAndLossReportPage = () => {
  const { t } = useTranslation("reports");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useRecoilValue(authState);
  const companyName = auth.user?.company?.name;

  const formValues = useMemo((): ProfitAndLossFilters => {
    return {
      startDate:
        searchParams.get("start_date") ||
        moment().startOf("month").format("yyyy-MM-DD"),
      endDate:
        searchParams.get("end_date") ||
        moment().endOf("month").format("yyyy-MM-DD"),
      branchId: searchParams.get("branch_id") || "",
    };
  }, [searchParams]);

  const { control, setValue } = useForm<ProfitAndLossFilters>({
    values: formValues,
  });

  const { startDate, endDate, branchId: branchIdParam } = formValues;

  const { data: branchesData } = useGetBranches();
  const branchesHavePermission = branchesData?.data || [];

  const [queryParams, setQueryParams] = useState<QueryParams>(() =>
    buildQueryParams(startDate, endDate, branchIdParam),
  );

  const {
    data: reportResponse,
    isFetching,
  } = useGetProfitAndLossReportData({
    ...queryParams,
    enabled: true,
  });

  const {
    refetch: refetchDownloadReport,
    isFetching: isDownloadingReport,
  } = useGetProfitAndLossReportDownload({
    ...queryParams,
    enabled: false,
  });

  const handleUpdate = useCallback(() => {
    setQueryParams(buildQueryParams(startDate, endDate, branchIdParam));
  }, [startDate, endDate, branchIdParam]);

  const handleDownload = () => {
    void refetchDownloadReport().then((res) => {
      if (res.data != null) {
        downloadAsXLS(`\uFEFF${res.data}`, "profit-and-loss");
      }
    });
  };

  const payload = reportResponse?.data;

  const syncDatesToUrl = (start: string, end: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("start_date", start);
    next.set("end_date", end);
    setSearchParams(next);
  };

  const syncBranchToUrl = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("branch_id", id);
    else next.delete("branch_id");
    setSearchParams(next);
  };

  const periodLabel = useMemo(() => {
    return t("reportDateRange", {
      start: moment(startDate).format("D MMM YYYY"),
      end: moment(endDate).format("D MMM YYYY"),
    });
  }, [t, startDate, endDate]);

  return (
    <Box p={{ xs: 2, sm: "12px 32px 32px" }} maxWidth="100%">
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        color="#101828"
        mb={1.5}
      >
        <Typography
          fontSize="0.9rem"
          color="#475467"
          onClick={() => navigate("/reports")}
          sx={{ cursor: "pointer" }}
        >
          {t("thirdEyeReport")}
        </Typography>
        <img src={RightArrow} alt="" style={{ width: 12, height: 12 }} />
        <Box borderRadius="4px" bgcolor="#EAECF0" px={1} py={0.5}>
          <Typography color="#344054" fontWeight={600} fontSize="0.85rem">
            {t("PLReport")}
          </Typography>
        </Box>
      </Stack>

      <Box mb={2}>
        <ReportControlBar
          left={
            <>
              <Box sx={{ minWidth: 250 }}>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <DateRangeInput
                      startDate={field.value ? moment(field.value) : null}
                      startDateId="pl-start"
                      endDate={
                        formValues.endDate ? moment(formValues.endDate) : null
                      }
                      endDateId="pl-end"
                      onDatesChange={(arg: {
                        startDate: moment.Moment | null;
                        endDate: moment.Moment | null;
                      }) => {
                        const s = arg.startDate?.format("yyyy-MM-DD");
                        const e = arg.endDate?.format("yyyy-MM-DD");
                        if (s && e) {
                          field.onChange(s);
                          setValue("endDate", e);
                          syncDatesToUrl(s, e);
                        }
                      }}
                    />
                  )}
                />
              </Box>

              <Controller
                name="branchId"
                control={control}
                render={({ field }) => (
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id="pl-branch" shrink>
                      {generalT("branch")}
                    </InputLabel>
                    <Select
                      {...field}
                      labelId="pl-branch"
                      label={generalT("branch")}
                      onChange={(e) => {
                        const v = e.target.value as string;
                        field.onChange(v);
                        syncBranchToUrl(v);
                      }}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>{t("allBranches")}</em>
                      </MenuItem>
                      {branchesHavePermission.map((branch) => (
                        <MenuItem key={branch.id} value={String(branch.id)}>
                          {branch.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <Button
                variant="contained"
                startIcon={<RefreshIcon fontSize="small" />}
                onClick={handleUpdate}
                disabled={isFetching}
                sx={{
                  height: 40,
                  minWidth: 120,
                  bgcolor: "#E97316",
                  "&:hover": { bgcolor: "#D2660F" },
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "8px",
                }}
              >
                {t("reportUpdate")}
              </Button>
            </>
          }
          right={
            <LoadingButton
              variant="outlined"
              startIcon={<FileDownloadIcon fontSize="small" />}
              onClick={handleDownload}
              loading={isDownloadingReport}
              disabled={!payload || isFetching}
              sx={{
                height: 40,
                minWidth: 100,
                borderColor: "#D0D5DD",
                color: "#344054",
              }}
            >
              {t("export")}
            </LoadingButton>
          }
        />
      </Box>

      <Box id="profit-and-loss-print-root">
        <ReportPageHeading
          title="Income Statement (Profit & Loss)"
          subtitle={companyName}
          periodLabel={periodLabel}
        />

        <ReportSectionCard>
          <ProfitAndLossTable data={payload} isLoading={isFetching} />
        </ReportSectionCard>
      </Box>
    </Box>
  );
};

export default ProfitAndLossReportPage;
